import {
  BookOpen,
  Building2,
  GraduationCap,
  Inbox,
  LayoutDashboard,
  Package,
  Plus,
  Settings,
  ShieldCheck,
  UserRound,
  Users,
} from '@lucide/vue'
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router'

import {
  createAuthGuard,
  MFA_CHALLENGE_ROUTE,
  MFA_SETUP_ROUTE,
  PORTAL_HOME_ROUTE,
  PUBLIC_KB_ROUTE,
  SIGN_IN_ROUTE,
  STAFF_HOME_ROUTE,
  type RouteAccess,
  type RouteNav,
} from '@/router/guard'
import { AI_GROUPS } from '@/components/staff/ai/ai'
import { NOT_FOUND_ROUTE, notFoundLocation } from '@/router/not-found'
import { useAuthStore, type Permission } from '@/stores/auth'

/** The two addresses of the settings load the same view. */
const SettingsView = () => import('@/views/staff/settings/SettingsView.vue')

interface Section {
  path: string
  name: string
  titleKey: string
  permission?: Permission
  nav?: RouteNav
}

/**
 * One page of an application. Its screen arrives with a later issue; until
 * then the shell shows the page header and one sentence. The permission and
 * the navigation entry are final, so the guard and the navigation are too.
 */
function section(access: RouteAccess, page: Section): RouteRecordRaw {
  return {
    path: page.path,
    name: page.name,
    component: () => import('@/views/PlaceholderView.vue'),
    meta: { access, titleKey: page.titleKey, permission: page.permission, nav: page.nav },
  }
}

const AccountView = () => import('@/views/account/AccountView.vue')
const STAFF_ACCOUNT_PAGES = [
  { name: 'staff.account', label: 'profile' },
  { name: 'staff.account.skills', label: 'skills' },
  { name: 'staff.account.notifications', label: 'mail' },
  { name: 'staff.account.apiTokens', label: 'tokens' },
] as const

/**
 * The first page of the staff application for a user: the ticket list, else
 * the dashboard of an admin, else the own account, which every staff user has.
 */
export function staffLanding(can: (permission: Permission) => boolean): string {
  if (can('tickets.view')) return 'staff.tickets'
  if (can('settings.manage')) return 'staff.dashboard'
  return 'staff.account'
}

/**
 * The characters of an article slug in the contract. The router takes no
 * group in a parameter pattern, so the API checks the hyphens: an address
 * with another character below /articles or /kb is the not-found view, with
 * no call to the API.
 */
const SLUG = '[a-z0-9][a-z0-9-]*'

/** The `id` of a route as a number. The pattern of the path lets only digits through. */
function idOf(route: RouteLocationNormalized): { id: number } {
  const id = Number(route.params.id)
  if (!Number.isSafeInteger(id)) {
    throw new Error(
      `The route ${String(route.name)} has no numeric id: ${String(route.params.id)}.`,
    )
  }
  return { id }
}

// Two shells, one SPA (MRS 7.1): staff under `/`, customers under `/portal`.
// Every view is lazy so a shell only loads what it shows. `meta.access` says
// who may open a route and `meta.permission` narrows it (MRS 3.2);
// src/router/guard.ts makes the decision. A route with `meta.nav` is an entry
// of the navigation of its shell (src/shell/navigation.ts).
//
// The sign-in paths are the ones that tendly-api writes into its mails and
// into the Google callback (App\Auth\FrontendUrls). A change of a path here
// breaks those links.
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/StaffLayout.vue'),
    meta: { access: 'staff' },
    children: [
      {
        // The start of the staff application is the ticket list (MRS 8.1),
        // or the first page that the user may open. The record has a view
        // only because the router wants one: `beforeEnter` always leaves.
        path: '',
        name: STAFF_HOME_ROUTE,
        component: () => import('@/views/NotFoundView.vue'),
        beforeEnter: () => ({ name: staffLanding(useAuthStore().can), replace: true }),
        meta: { access: 'staff' },
      },
      {
        path: 'dashboard',
        name: 'staff.dashboard',
        component: () => import('@/views/staff/dashboard/DashboardView.vue'),
        meta: {
          access: 'staff',
          permission: 'settings.manage',
          titleKey: 'staff.dashboard.title',
          nav: { icon: LayoutDashboard, order: 10 },
        },
      },
      {
        // The ticket screens (MRS 6.4). The list is the entry of the
        // navigation; the page of one ticket keeps that entry marked.
        path: 'tickets',
        meta: { access: 'staff', permission: 'tickets.view' },
        children: [
          {
            path: '',
            name: 'staff.tickets',
            component: () => import('@/views/staff/tickets/TicketsListView.vue'),
            meta: {
              access: 'staff',
              permission: 'tickets.view',
              titleKey: 'staff.tickets.title',
              nav: { icon: Inbox, order: 20 },
            },
          },
          {
            path: ':id(\\d+)',
            name: 'staff.tickets.show',
            component: () => import('@/views/staff/tickets/TicketView.vue'),
            props: idOf,
            meta: {
              access: 'staff',
              permission: 'tickets.view',
              titleKey: 'tickets.detail.title',
            },
          },
        ],
      },
      {
        // The companies admin screens (MRS 6.2). The list is the entry of the
        // navigation; the pages below it keep that entry marked.
        path: 'companies',
        meta: { access: 'staff', permission: 'companies.manage' },
        children: [
          {
            path: '',
            name: 'staff.companies',
            component: () => import('@/views/staff/companies/CompaniesListView.vue'),
            meta: {
              access: 'staff',
              permission: 'companies.manage',
              titleKey: 'staff.companies.title',
              nav: { icon: Building2, order: 30 },
            },
          },
          {
            path: 'new',
            name: 'staff.companies.new',
            component: () => import('@/views/staff/companies/CompanyFormView.vue'),
            props: { id: null },
            meta: {
              access: 'staff',
              permission: 'companies.manage',
              titleKey: 'companies.new.title',
            },
          },
          {
            path: ':id(\\d+)',
            name: 'staff.companies.show',
            component: () => import('@/views/staff/companies/CompanyDetailView.vue'),
            props: idOf,
            meta: {
              access: 'staff',
              permission: 'companies.manage',
              titleKey: 'companies.detail.title',
            },
          },
          {
            path: ':id(\\d+)/edit',
            name: 'staff.companies.edit',
            component: () => import('@/views/staff/companies/CompanyFormView.vue'),
            props: idOf,
            meta: {
              access: 'staff',
              permission: 'companies.manage',
              titleKey: 'companies.edit.title',
            },
          },
        ],
      },
      {
        // The products admin screens (MRS 6.3). The repositories and the
        // mapped companies of a product are tabs of its page.
        path: 'products',
        meta: { access: 'staff', permission: 'products.manage' },
        children: [
          {
            path: '',
            name: 'staff.products',
            component: () => import('@/views/staff/products/ProductsListView.vue'),
            meta: {
              access: 'staff',
              permission: 'products.manage',
              titleKey: 'staff.products.title',
              nav: { icon: Package, order: 40 },
            },
          },
          {
            path: 'new',
            name: 'staff.products.new',
            component: () => import('@/views/staff/products/ProductFormView.vue'),
            props: { id: null },
            meta: {
              access: 'staff',
              permission: 'products.manage',
              titleKey: 'products.new.title',
            },
          },
          {
            path: ':id(\\d+)',
            name: 'staff.products.show',
            component: () => import('@/views/staff/products/ProductDetailView.vue'),
            props: idOf,
            meta: {
              access: 'staff',
              permission: 'products.manage',
              titleKey: 'products.detail.title',
            },
          },
          {
            path: ':id(\\d+)/edit',
            name: 'staff.products.edit',
            component: () => import('@/views/staff/products/ProductFormView.vue'),
            props: idOf,
            meta: {
              access: 'staff',
              permission: 'products.manage',
              titleKey: 'products.edit.title',
            },
          },
        ],
      },
      {
        // The users admin screens (MRS 6.2). The invites are a tab of the list.
        path: 'users',
        meta: { access: 'staff', permission: 'users.manage' },
        children: [
          {
            path: '',
            name: 'staff.users',
            component: () => import('@/views/staff/users/UsersListView.vue'),
            meta: {
              access: 'staff',
              permission: 'users.manage',
              titleKey: 'staff.users.title',
              nav: { icon: Users, order: 50 },
            },
          },
          {
            path: ':id(\\d+)',
            name: 'staff.users.show',
            component: () => import('@/views/staff/users/UserDetailView.vue'),
            props: idOf,
            meta: { access: 'staff', permission: 'users.manage', titleKey: 'users.detail.title' },
          },
        ],
      },
      {
        // The roles admin screens (MRS 3.2): a role is a set of permissions.
        path: 'roles',
        meta: { access: 'staff', permission: 'roles.manage' },
        children: [
          {
            path: '',
            name: 'staff.roles',
            component: () => import('@/views/staff/roles/RolesListView.vue'),
            meta: {
              access: 'staff',
              permission: 'roles.manage',
              titleKey: 'staff.roles.title',
              nav: { icon: ShieldCheck, order: 55 },
            },
          },
          {
            path: 'new',
            name: 'staff.roles.new',
            component: () => import('@/views/staff/roles/RoleFormView.vue'),
            props: { id: null },
            meta: { access: 'staff', permission: 'roles.manage', titleKey: 'roles.new.title' },
          },
          {
            path: ':id(\\d+)',
            name: 'staff.roles.show',
            component: () => import('@/views/staff/roles/RoleFormView.vue'),
            props: idOf,
            meta: { access: 'staff', permission: 'roles.manage', titleKey: 'roles.edit.title' },
          },
        ],
      },
      {
        // The skills vocabulary (MRS 6.8). An agent sets the own skills in the
        // account; this page is the list that those skills come from.
        path: 'skills',
        name: 'staff.skills',
        component: () => import('@/views/staff/skills/SkillsListView.vue'),
        meta: {
          access: 'staff',
          permission: 'skills.manage',
          titleKey: 'staff.skills.title',
          nav: { icon: GraduationCap, order: 47 },
        },
      },
      {
        // The knowledge base of staff (MRS 6.12): every visibility and every
        // status. A writer with `articles.manage` writes and publishes there.
        path: 'articles',
        meta: { access: 'staff', permission: 'articles.view' },
        children: [
          {
            path: '',
            name: 'staff.articles',
            component: () => import('@/views/staff/articles/ArticlesListView.vue'),
            meta: {
              access: 'staff',
              permission: 'articles.view',
              titleKey: 'staff.articles.title',
              nav: { icon: BookOpen, order: 45 },
            },
          },
          {
            path: 'new',
            name: 'staff.articles.new',
            component: () => import('@/views/staff/articles/ArticleFormView.vue'),
            props: { id: null },
            meta: {
              access: 'staff',
              permission: 'articles.manage',
              titleKey: 'staff.articles.new',
            },
          },
          {
            path: ':id(\\d+)/edit',
            name: 'staff.articles.edit',
            component: () => import('@/views/staff/articles/ArticleFormView.vue'),
            props: idOf,
            meta: {
              access: 'staff',
              permission: 'articles.manage',
              titleKey: 'staff.articles.edit',
            },
          },
          {
            path: `:slug(${SLUG})`,
            name: 'staff.articles.show',
            component: () => import('@/views/staff/articles/ArticleReaderView.vue'),
            props: true,
            meta: {
              access: 'staff',
              permission: 'articles.view',
              titleKey: 'staff.articles.reader',
            },
          },
        ],
      },
      {
        path: 'settings',
        name: 'staff.settings',
        component: SettingsView,
        meta: {
          access: 'staff',
          permission: 'settings.manage',
          titleKey: 'staff.settings.title',
          view: 'settings',
          nav: { icon: Settings, order: 60 },
        },
      },
      {
        // The AI tab of the settings, one address for each of its groups. It
        // is the same view as `/settings`, so a change in the form stays when
        // the user moves between the tabs and the groups. Staff without `ai.settings` have no AI tab.
        path: `settings/ai/:group(${AI_GROUPS.join('|')})?`,
        name: 'staff.settings.ai',
        component: SettingsView,
        beforeEnter: (to) => useAuthStore().can('ai.settings') || notFoundLocation(to),
        meta: {
          access: 'staff',
          permission: 'settings.manage',
          titleKey: 'staff.settings.title',
          view: 'settings',
        },
      },
      {
        // The AI dashboard (MRS 6.10) is under the settings: its name keeps
        // that entry of the navigation marked. It has its own permission.
        path: 'settings/ai/dashboard',
        name: 'staff.settings.aiDashboard',
        component: () => import('@/views/staff/settings/AiDashboardView.vue'),
        meta: {
          access: 'staff',
          permission: 'ai.settings',
          titleKey: 'staff.aiDashboard.title',
        },
      },
      {
        // The own account of a staff user (MRS 8.1): every staff user has
        // one, so no permission narrows it. Two-step sign-in is a duty for
        // staff (MRS 6.1), so the account has no switch for it.
        path: 'account',
        meta: { access: 'staff' },
        children: [
          {
            path: '',
            name: 'staff.account',
            component: AccountView,
            props: {
              sections: ['details', 'password', 'availability'],
              descriptionKey: 'account.staff.description',
              pages: STAFF_ACCOUNT_PAGES,
            },
            meta: { access: 'staff', titleKey: 'account.staff.title' },
          },
          {
            // The own skills of an agent (MRS 6.8), for `skills.self`.
            path: 'skills',
            name: 'staff.account.skills',
            component: AccountView,
            props: {
              sections: ['skills'],
              descriptionKey: 'account.staff.skillsDescription',
              pages: STAFF_ACCOUNT_PAGES,
            },
            meta: {
              access: 'staff',
              permission: 'skills.self',
              titleKey: 'account.staff.skillsTitle',
            },
          },
          {
            path: 'notifications',
            name: 'staff.account.notifications',
            component: AccountView,
            props: {
              sections: ['mail'],
              descriptionKey: 'account.staff.mailDescription',
              pages: STAFF_ACCOUNT_PAGES,
            },
            meta: { access: 'staff', titleKey: 'account.staff.mailTitle' },
          },
          {
            // The own API tokens and how to connect an MCP client (MRS 6.9).
            // Staff only: a customer user gets not-found from the guard.
            path: 'api-tokens',
            name: 'staff.account.apiTokens',
            component: AccountView,
            props: {
              sections: ['apiTokens', 'mcp'],
              descriptionKey: 'account.staff.tokensDescription',
              pages: STAFF_ACCOUNT_PAGES,
            },
            meta: { access: 'staff', titleKey: 'account.staff.tokensTitle' },
          },
        ],
      },
    ],
  },
  {
    path: '/portal',
    component: () => import('@/layouts/PortalLayout.vue'),
    meta: { access: 'portal' },
    children: [
      section('portal', { path: '', name: PORTAL_HOME_ROUTE, titleKey: 'portal.home.title' }),
      {
        // The ticket screens of the portal (MRS 6.7). The list and the form
        // are entries of the navigation; the page of one ticket keeps the
        // list entry marked.
        path: 'tickets',
        meta: { access: 'portal', permission: 'portal.tickets.view' },
        children: [
          {
            path: '',
            name: 'portal.tickets',
            component: () => import('@/views/portal/PortalTicketsView.vue'),
            meta: {
              access: 'portal',
              permission: 'portal.tickets.view',
              titleKey: 'portal.tickets.title',
              nav: { icon: Inbox, order: 10 },
            },
          },
          {
            path: 'new',
            name: 'portal.tickets.new',
            component: () => import('@/views/portal/PortalNewTicketView.vue'),
            meta: {
              access: 'portal',
              permission: 'portal.tickets.create',
              titleKey: 'portal.newTicket.title',
              nav: { icon: Plus, order: 20 },
            },
          },
          {
            path: ':id(\\d+)',
            name: 'portal.tickets.show',
            component: () => import('@/views/portal/PortalTicketView.vue'),
            props: idOf,
            meta: {
              access: 'portal',
              permission: 'portal.tickets.view',
              titleKey: 'portal.ticket.title',
            },
          },
        ],
      },
      {
        // The help articles of the portal (MRS 6.7): published articles for
        // customers and for everyone, from the portal endpoint only.
        path: 'articles',
        meta: { access: 'portal', permission: 'portal.articles.view' },
        children: [
          {
            path: '',
            name: 'portal.articles',
            component: () => import('@/views/portal/PortalArticlesView.vue'),
            meta: {
              access: 'portal',
              permission: 'portal.articles.view',
              titleKey: 'portal.articles.title',
              nav: { icon: BookOpen, order: 25 },
            },
          },
          {
            path: `:slug(${SLUG})`,
            name: 'portal.articles.show',
            component: () => import('@/views/portal/PortalArticleView.vue'),
            props: true,
            meta: {
              access: 'portal',
              permission: 'portal.articles.view',
              titleKey: 'portal.articles.article',
            },
          },
        ],
      },
      {
        path: 'profile',
        name: 'portal.profile',
        component: AccountView,
        props: {
          sections: ['details', 'password', 'mail', 'mfa'],
          descriptionKey: 'account.description',
        },
        meta: {
          access: 'portal',
          permission: 'portal.profile',
          titleKey: 'portal.profile.title',
          nav: { icon: UserRound, order: 30 },
        },
      },
    ],
  },
  {
    // The public help centre (MRS 6.12): no sign-in, and the same pages for a
    // visitor and a signed-in user. It shows what the public endpoint gives.
    path: '/kb',
    component: () => import('@/layouts/PublicLayout.vue'),
    meta: { access: 'any' },
    children: [
      {
        path: '',
        name: PUBLIC_KB_ROUTE,
        component: () => import('@/views/public/PublicArticlesView.vue'),
        meta: { access: 'any', titleKey: 'kb.title' },
      },
      {
        path: `:slug(${SLUG})`,
        name: 'public.kb.show',
        component: () => import('@/views/public/PublicArticleView.vue'),
        props: true,
        meta: { access: 'any', titleKey: 'kb.article' },
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/layouts/AuthLayout.vue'),
    meta: { access: 'guest' },
    children: [
      {
        path: 'sign-in',
        name: SIGN_IN_ROUTE,
        component: () => import('@/views/auth/SignInView.vue'),
        meta: { access: 'guest' },
      },
      {
        // With `?token=` the page signs the customer in; without it the page asks for the link.
        path: 'sign-in/magic',
        name: 'auth.magic-link',
        component: () => import('@/views/auth/MagicLinkView.vue'),
        meta: { access: 'link' },
      },
      {
        path: 'mfa/setup',
        name: MFA_SETUP_ROUTE,
        component: () => import('@/views/auth/MfaSetupView.vue'),
        meta: { access: 'mfa-setup' },
      },
      {
        // A signed-in customer turns two-step sign-in on from the profile
        // (MRS 6.1: optional for customers). The page is the set-up page of
        // the sign-in, so it stands outside the portal shell.
        path: 'account/mfa/setup',
        name: 'portal.mfa.setup',
        component: () => import('@/views/auth/MfaSetupView.vue'),
        meta: { access: 'portal', permission: 'portal.profile' },
      },
      {
        path: 'mfa/challenge',
        name: MFA_CHALLENGE_ROUTE,
        component: () => import('@/views/auth/MfaChallengeView.vue'),
        meta: { access: 'mfa-challenge' },
      },
      {
        path: 'mfa/recovery',
        name: 'auth.mfa.recovery',
        component: () => import('@/views/auth/MfaRecoveryView.vue'),
        meta: { access: 'mfa-challenge' },
      },
      {
        path: 'password/forgot',
        name: 'auth.password.forgot',
        component: () => import('@/views/auth/PasswordForgotView.vue'),
        meta: { access: 'guest' },
      },
      {
        path: 'password/reset',
        name: 'auth.password.reset',
        component: () => import('@/views/auth/PasswordResetView.vue'),
        meta: { access: 'link' },
      },
      {
        path: 'invites/accept',
        name: 'auth.invite.accept',
        component: () => import('@/views/auth/InviteAcceptView.vue'),
        meta: { access: 'link' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: NOT_FOUND_ROUTE,
    component: () => import('@/views/NotFoundView.vue'),
    meta: { access: 'any', titleKey: 'notFound.title' },
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(createAuthGuard(() => useAuthStore()))
